import React, { useEffect, useMemo, useState } from "react";
import Box from "@mui/material/Box";
import CircularProgress from "@mui/material/CircularProgress";
import CrossService from "../puzzles/crossword/CrossService";
import {
    buildCrossMeaningsMapsFromDisplayClue,
    lookupCrossWordMeaningPreview,
} from "../puzzles/crossword/crossMeaningPreviewUtils";
import { WEB_AD_CROSSWORD_ID } from "./webAdShots";

const FONT = '"Segoe UI", "Helvetica Neue", Arial, "Noto Sans", sans-serif';
const INK = "#0F172A";
const ACCENT = "#0EA5E9";
const GRID_SIZE = 12;

function parseFilledCoordinates(puzzle) {
    const raw =
        puzzle?.filledCoordinates
        ?? puzzle?.template?.filledCoordinates
        ?? puzzle?.filled_coordinates
        ?? puzzle?.template?.filled_coordinates;
    if (!raw) return [];
    if (Array.isArray(raw)) return raw;
    try {
        const parsed = JSON.parse(raw);
        return Array.isArray(parsed) ? parsed : [];
    } catch {
        return [];
    }
}

function getFirstPosition(coord) {
    if (!coord?.positions?.length || coord.direction === "scattered") return null;
    if (coord.reversed) {
        return coord.positions[coord.positions.length - 1];
    }
    if (coord.direction === "vertical") {
        return coord.positions.reduce((min, pos) => (pos.row < min.row ? pos : min));
    }
    if (coord.direction === "horizontal") {
        return coord.positions.reduce((min, pos) => (pos.col < min.col ? pos : min));
    }
    return coord.positions[0];
}

function buildWordNumberMap(filledCoordinates) {
    const startingPositions = new Map();
    filledCoordinates.forEach((coord) => {
        const firstPos = getFirstPosition(coord);
        if (!firstPos) return;
        const cellKey = `${firstPos.row},${firstPos.col}`;
        if (!startingPositions.has(cellKey)) {
            startingPositions.set(cellKey, { row: firstPos.row, col: firstPos.col });
        }
    });
    const sorted = Array.from(startingPositions.values()).sort((a, b) => {
        if (a.row !== b.row) return a.row - b.row;
        return a.col - b.col;
    });
    const numbers = new Map();
    sorted.forEach((pos, index) => {
        numbers.set(`${pos.row},${pos.col}`, index + 1);
    });
    return numbers;
}

function dirLabel(direction) {
    if (direction === "vertical") return "Down";
    if (direction === "horizontal") return "Across";
    return "Across";
}

function buildSolutionGrid(puzzle, filledCoordinates) {
    const grid = Array.from({ length: GRID_SIZE }, () =>
        Array.from({ length: GRID_SIZE }, () => " "),
    );
    if (puzzle?.letters) {
        const lines = puzzle.letters.split("\n").slice(0, GRID_SIZE);
        while (lines.length < GRID_SIZE) lines.push("");
        lines.forEach((line, r) => {
            const chars = line.split("").map((c) => c.toUpperCase());
            while (chars.length < GRID_SIZE) chars.push(" ");
            grid[r] = chars.slice(0, GRID_SIZE);
        });
    }
    filledCoordinates.forEach((coordinate) => {
        coordinate.positions?.forEach((pos) => {
            if (pos.value != null && grid[pos.row]?.[pos.col] !== undefined) {
                grid[pos.row][pos.col] = String(pos.value).toUpperCase();
            }
        });
    });
    return grid;
}

/**
 * Live crossword board + clue input for web Google Ads.
 * Puzzle: {@link WEB_AD_CROSSWORD_ID} (`/puzzle/play-cross/:id`).
 */
export default function WebAdCrosswordBoard({ size = 360, showInput = true }) {
    const [puzzle, setPuzzle] = useState(null);
    const [failed, setFailed] = useState(false);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const data = await CrossService.getPuzzleById(WEB_AD_CROSSWORD_ID);
                if (cancelled) return;
                if (!data) {
                    setFailed(true);
                    return;
                }
                setPuzzle(data);
            } catch {
                if (!cancelled) setFailed(true);
            }
        })();
        return () => {
            cancelled = true;
        };
    }, []);

    const filledCoordinates = useMemo(() => parseFilledCoordinates(puzzle), [puzzle]);
    const playableCoords = useMemo(
        () => filledCoordinates.filter((c) => c?.direction !== "scattered" && c?.positions?.length),
        [filledCoordinates],
    );
    const wordNumbers = useMemo(() => buildWordNumberMap(playableCoords), [playableCoords]);
    const solutionGrid = useMemo(
        () => (puzzle ? buildSolutionGrid(puzzle, filledCoordinates) : null),
        [puzzle, filledCoordinates],
    );

    const foundWords = useMemo(() => {
        const n = Math.max(1, Math.floor(playableCoords.length / 2));
        return playableCoords.slice(0, n);
    }, [playableCoords]);

    const activeCoord = useMemo(() => {
        const foundKeys = new Set(
            foundWords.map((c) => String(c.text || c.word || "").toLowerCase().trim()),
        );
        return (
            playableCoords.find((c) => {
                const key = String(c.text || c.word || "").toLowerCase().trim();
                return key && !foundKeys.has(key);
            })
            || playableCoords[0]
            || null
        );
    }, [playableCoords, foundWords]);

    const clueMaps = useMemo(() => {
        const raw =
            puzzle?.displayClue
            ?? puzzle?.display_clue
            ?? puzzle?.template?.displayClue
            ?? puzzle?.template?.display_clue
            ?? null;
        return buildCrossMeaningsMapsFromDisplayClue(Array.isArray(raw) ? raw : []);
    }, [puzzle]);

    const activeMeta = useMemo(() => {
        if (!activeCoord) return null;
        const first = getFirstPosition(activeCoord);
        const number = first ? wordNumbers.get(`${first.row},${first.col}`) : null;
        const word = String(activeCoord.text || activeCoord.word || "").toUpperCase();
        const clue =
            lookupCrossWordMeaningPreview(
                clueMaps.wordMap,
                clueMaps.clueMap,
                activeCoord.text || activeCoord.word,
                activeCoord.displayWord,
                number,
                activeCoord.direction,
            ) || "Fill the board one letter at a time";
        return {
            number,
            direction: dirLabel(activeCoord.direction),
            word,
            clue: clue.length > 72 ? `${clue.slice(0, 69)}…` : clue,
            cells: new Set(
                (activeCoord.positions || []).map((p) => `${p.row},${p.col}`),
            ),
        };
    }, [activeCoord, wordNumbers, clueMaps]);

    const revealedKeys = useMemo(() => {
        const keys = new Set();
        foundWords.forEach((coord) => {
            (coord.positions || []).forEach((p) => keys.add(`${p.row},${p.col}`));
        });
        return keys;
    }, [foundWords]);

    const puzzleCells = useMemo(() => {
        const keys = new Set();
        playableCoords.forEach((coord) => {
            (coord.positions || []).forEach((p) => keys.add(`${p.row},${p.col}`));
        });
        return keys;
    }, [playableCoords]);

    const bounds = useMemo(() => {
        let minRow = GRID_SIZE;
        let maxRow = 0;
        let minCol = GRID_SIZE;
        let maxCol = 0;
        puzzleCells.forEach((key) => {
            const [r, c] = key.split(",").map(Number);
            minRow = Math.min(minRow, r);
            maxRow = Math.max(maxRow, r);
            minCol = Math.min(minCol, c);
            maxCol = Math.max(maxCol, c);
        });
        if (puzzleCells.size === 0) {
            return { minRow: 0, maxRow: 7, minCol: 0, maxCol: 7 };
        }
        return { minRow, maxRow, minCol, maxCol };
    }, [puzzleCells]);

    const visibleRows = bounds.maxRow - bounds.minRow + 1;
    const visibleCols = bounds.maxCol - bounds.minCol + 1;
    const cell = Math.floor(size / Math.max(visibleRows, visibleCols, 1));
    const gridW = cell * visibleCols;
    const gridH = cell * visibleRows;

    if (failed) {
        return (
            <Box
                sx={{
                    width: size,
                    height: size,
                    borderRadius: 2,
                    bgcolor: "#E2E8F0",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    color: "#64748B",
                    fontSize: 14,
                    fontWeight: 600,
                    textAlign: "center",
                    px: 2,
                }}
            >
                Could not load crossword
            </Box>
        );
    }

    if (!puzzle || !solutionGrid) {
        return (
            <Box
                sx={{
                    width: size,
                    height: size,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                }}
            >
                <CircularProgress size={36} sx={{ color: "#3BA666" }} />
            </Box>
        );
    }

    return (
        <Box
            data-web-ad-crossword="1"
            sx={{ display: "flex", flexDirection: "column", gap: 1.25, alignItems: "center" }}
        >
            <Box
                sx={{
                    width: gridW,
                    height: gridH,
                    display: "grid",
                    gridTemplateColumns: `repeat(${visibleCols}, ${cell}px)`,
                    gridTemplateRows: `repeat(${visibleRows}, ${cell}px)`,
                    border: `2px solid ${INK}`,
                    bgcolor: INK,
                    gap: "1px",
                    boxShadow: "0 14px 32px rgba(15,23,42,0.16)",
                    boxSizing: "content-box",
                }}
            >
                {Array.from({ length: visibleRows }, (_, rowOffset) =>
                    Array.from({ length: visibleCols }, (_, colOffset) => {
                        const r = bounds.minRow + rowOffset;
                        const c = bounds.minCol + colOffset;
                        const key = `${r},${c}`;
                        const on = puzzleCells.has(key);
                        const isActive = activeMeta?.cells.has(key);
                        const showLetter = on && revealedKeys.has(key);
                        const letter = showLetter ? solutionGrid[r]?.[c] || "" : "";
                        const number = wordNumbers.get(key);
                        return (
                            <Box
                                key={key}
                                sx={{
                                    width: cell,
                                    height: cell,
                                    position: "relative",
                                    bgcolor: !on ? INK : isActive ? "#E0F2FE" : "#fff",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    fontFamily: FONT,
                                    fontWeight: 800,
                                    fontSize: Math.round(cell * 0.45),
                                    color: INK,
                                    boxSizing: "border-box",
                                    outline: isActive ? `2px solid ${ACCENT}` : "none",
                                    outlineOffset: -2,
                                }}
                            >
                                {on && number != null ? (
                                    <Box
                                        component="span"
                                        sx={{
                                            position: "absolute",
                                            top: 1,
                                            left: 2,
                                            fontSize: Math.max(8, Math.round(cell * 0.22)),
                                            fontWeight: 700,
                                            lineHeight: 1,
                                            color: "#475569",
                                        }}
                                    >
                                        {number}
                                    </Box>
                                ) : null}
                                {letter && letter !== " " ? letter : ""}
                            </Box>
                        );
                    }),
                )}
            </Box>
            {showInput && activeMeta ? (
                <Box
                    sx={{
                        width: Math.max(gridW, Math.round(size * 0.92)),
                        minHeight: Math.round(cell * 1.15),
                        bgcolor: "#fff",
                        borderRadius: 1.5,
                        border: `2px solid ${ACCENT}`,
                        display: "flex",
                        alignItems: "center",
                        px: 2,
                        py: 0.75,
                        fontFamily: FONT,
                        fontWeight: 700,
                        fontSize: Math.max(14, Math.round(cell * 0.36)),
                        color: INK,
                        boxShadow: "0 8px 20px rgba(14,165,233,0.2)",
                        boxSizing: "border-box",
                        lineHeight: 1.25,
                    }}
                >
                    {activeMeta.number != null ? `${activeMeta.number} ` : ""}
                    {activeMeta.direction} · {activeMeta.clue}
                    <Box component="span" sx={{ color: ACCENT, ml: 1 }}>
                        {activeMeta.word}
                    </Box>
                </Box>
            ) : null}
        </Box>
    );
}
